import { useCallback, useEffect, useState } from "react";
import { Box, Button, FormControlLabel, Switch } from "@mui/material";
import { DocumentContentActions } from "../documentsApi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { unwrapResult } from "@reduxjs/toolkit";
import { Localized } from "@fluent/react";
import { useNavigate } from "react-router-dom";
import { DocumentTest, loadDocumentAllTests } from "./documentTestsApi";
import { PrintPreviewDocumentTestView } from "./PrintPreviewDocumentTestView";
import { DocumentTestsFlatTable } from "./DocumentTestsFlatTable";
import { EditDocumentTestDialog } from "./dialogs/EditDocumentTestDialog";
import AddIcon from '@mui/icons-material/Add';
import { DeleteDocumentTest } from "./dialogs/DeleteDocumentTest";
import { TestResultDialog } from "./dialogs/TestResultDialog";
import { selectDocument } from "../documentSlice";
import { impossible } from "../../../app/Utils";
import { DeviationsLoader } from "../sections/deviationsSection/DeviationsLoader";
import { TestPhaseType } from "../../testSpecification/testSpecificationApi";
import { defRAFunction } from "../riskAnalysis/riskAnalysisApi";

export interface DocumentTestViewProps {
    documentId: number
    sectionId: string
    actions: DocumentContentActions[],
    enablePreview?: boolean
}

function hasAction(
    actions: DocumentContentActions[],
    action: DocumentContentActions
): boolean {
    return actions.includes(action);
}

type DialogState
    = { tag: "none" }
    | { tag: "addTest" }
    | { tag: "editTest", testId: number }
    | { tag: "deleteTest", testId: number }
    | { tag: "testResult", testId: number }
    | { tag: "newTestRun", testId: number }


export const DocumentTestView = ({ documentId, sectionId, actions, enablePreview = false }: DocumentTestViewProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const loaded = useAppSelector(state => selectDocument(state).doc?.tests.loaded ?? false)

    const editable = hasAction(actions, "Edit") && header.documentKind !== 'REPORT'

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [isPreview, setIsPreview] = useState(false);
    const [dialog, setDialog] = useState<DialogState>({ tag: "none" })

    useEffect(() => {
        if (!loaded) {
            setIsLoading(true)
            dispatch(loadDocumentAllTests({ documentId: documentId }))
                .then(unwrapResult)
                .catch((error) => { console.error(error); setIsError(true); setError(error); })
                .finally(() => setIsLoading(false))
        }
    }, [documentId, loaded])

    // ======================================================
    //  Handlers
    // ======================================================
    const handleAddTest = () => {
        setDialog({ tag: "addTest" });
    }

    const handleOpenTest = (documentTestId: number) => {
        setDialog({ tag: "editTest", testId: documentTestId });
    };

    const handleOpenTestResult = (documentTestId: number) => {
        setDialog({ tag: "testResult", testId: documentTestId });
    };

    const handleDelete = (documentTestId: number) => {
        setDialog({ tag: "deleteTest", testId: documentTestId });
    };

    const handleDialogCancelClick = useCallback(() => {
        setDialog({ tag: "none" })
    }, [])

    const onSuccessfullySave = (test: DocumentTest) => {
        setDialog({ tag: "none" })
    };

    const onSuccessfullyDelete = (testId: number) => {
        setDialog({ tag: "none" });
    };

	let stage: TestPhaseType | undefined = undefined
	switch (header.type) {
		case 'DQ':
		case 'IQ':
		case 'PQ':
		case 'OQ':
			stage = header.type	
			break;
	}

    // ======================================================
    //  Render
    // ======================================================
    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }


    return (
        <DeviationsLoader>
            {enablePreview && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                    <FormControlLabel
                        control={<Switch checked={isPreview} onChange={() => setIsPreview(v => !v)} />}
                        label={<Localized id="print-preview">
                            <span>_Print preview</span>
                        </Localized>}
                    />
                </Box>
            )}
            {enablePreview && isPreview ? (
                <PrintPreviewDocumentTestView />
            ) : (
                <>
                    <DocumentTestsFlatTable sectionId={sectionId} onOpenTest={handleOpenTest} onOpenTestResult={handleOpenTestResult} onDeleteTest={handleDelete} actions={actions} />
                        {editable  && <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        size="small"
                        sx={{ m: 1 }}
                        onClick={() => handleAddTest()}
                    >
                        <Localized id="document-tests-add">
                            <span>_add test</span>
                        </Localized>
                    </Button>}
                    {
                        dialog.tag === "addTest" &&
                        <EditDocumentTestDialog
                            mode="create"
                            onSave={onSuccessfullySave}
                            onCancel={handleDialogCancelClick}
                            documentId={documentId}
							stage={stage}
                            sectionId={sectionId} />
                    }
                    {
                        dialog.tag === "editTest" &&
                        <EditDocumentTestDialog
                            testId={dialog.testId}
                            onCancel={handleDialogCancelClick}
                            onSave={onSuccessfullySave}
                            documentId={documentId}
                            sectionId={sectionId}
							stage={stage}
                            mode="edit"
                        />
                    }
                    {
                        dialog.tag === "deleteTest" &&
                        <DeleteDocumentTest
                            documentId={documentId}
                            testId={dialog.testId}
                            onCancel={handleDialogCancelClick}
                            onSuccessfulDelete={onSuccessfullyDelete}
                        />
                    }
                    {
                        dialog.tag === "testResult" &&
                        <TestResultDialog
                            testId={dialog.testId}
                            onCancel={handleDialogCancelClick}
                            onSave={onSuccessfullySave}
                            documentId={documentId}
                            sectionId={sectionId}
                        />
                    }
                </>
            )}
        </DeviationsLoader>
    );
};

export default DocumentTestView;
